import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../api/client";
import { Banner } from "./forms";
import { pixelTheme } from "../../theme/pixel";
import { E32 } from "../../theme/endesga32";
import { simplePanelStyle } from "../../theme/simplePanel";
import { useResponsiveLayout } from "../../logic/useResponsiveLayout";

/**
 * Admin "Palette Snap" tool. Upload a PNG, see it side-by-side with
 * the Endesga-32-snapped version, download the result. The conversion
 * runs on the server via `tools/snap_palette.py`; nothing is persisted.
 */
export function PaletteSnapTool() {
  const theme = pixelTheme;
  const { narrow } = useResponsiveLayout();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [snappedUrl, setSnappedUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Revoke object URLs whenever they change so the browser can release
  // the underlying blobs. The cleanup runs on unmount and before each
  // re-assignment.
  useEffect(() => {
    return () => {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
    };
  }, [originalUrl]);

  useEffect(() => {
    return () => {
      if (snappedUrl) URL.revokeObjectURL(snappedUrl);
    };
  }, [snappedUrl]);

  const runSnap = useCallback(async (f: File) => {
    setBusy(true);
    setError(null);
    setSnappedUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob = await api.snapPalette(f);
      const url = URL.createObjectURL(blob);
      setSnappedUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  const onFile = useCallback(
    (f: File | null) => {
      if (!f) return;
      if (f.type && f.type !== "image/png") {
        setError(`Expected a PNG, got ${f.type || "unknown type"}.`);
        return;
      }
      setFile(f);
      setOriginalUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(f);
      });
      void runSnap(f);
    },
    [runSnap],
  );

  // Save with the exact same filename the user uploaded — the snapped
  // image is meant as a drop-in replacement.
  const downloadName = file?.name ?? "image.png";

  // Drag-and-drop: dropping a PNG anywhere on the tool body runs the
  // same code path as the CHOOSE PNG button. `dragDepth` is a counter
  // rather than a boolean because dragenter / dragleave fire for every
  // child element on the way in/out — a simple boolean would flicker.
  const [dragDepth, setDragDepth] = useState(0);
  const dragging = dragDepth > 0;

  const onDragEnter = useCallback((e: React.DragEvent) => {
    if (!hasFileItem(e)) return;
    e.preventDefault();
    setDragDepth((d) => d + 1);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!hasFileItem(e)) return;
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!hasFileItem(e)) return;
    // Required to mark the element as a valid drop target.
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (!hasFileItem(e)) return;
      e.preventDefault();
      setDragDepth(0);
      const f = e.dataTransfer.files?.[0] ?? null;
      onFile(f);
    },
    [onFile],
  );

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        // Visual feedback while a file is hovering over the tool — a
        // tinted outline around the whole pane, no layout shift.
        outline: dragging ? `3px dashed ${pixelTheme.accent}` : "none",
        outlineOffset: dragging ? "-3px" : "0",
      }}
    >
      <div
        style={{
          ...simplePanelStyle(theme),
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
          }}
        >
          PALETTE SNAP
        </div>
        <div
          style={{
            fontSize: 12,
            color: theme.inkFaint,
            lineHeight: 1.4,
          }}
        >
          Upload a PNG (or drag-and-drop one anywhere on this panel) and see it side-by-side with
          its Endesga-32 conversion. Click DOWNLOAD on the right pane to save the snapped image —
          it'll keep the original filename. Nothing is persisted on the server.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/png"
            style={{ display: "none" }}
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            style={pillButtonStyle(theme, busy)}
          >
            {file ? "CHANGE PNG" : "CHOOSE PNG"}
          </button>
          {file && (
            <button
              type="button"
              onClick={() => void runSnap(file)}
              disabled={busy}
              style={pillButtonStyle(theme, busy)}
            >
              {busy ? "SNAPPING…" : "RE-SNAP"}
            </button>
          )}
          {file && (
            <span
              style={{
                fontSize: 12,
                color: theme.inkFaint,
                wordBreak: "break-all",
              }}
            >
              {file.name}
            </span>
          )}
        </div>
      </div>

      {error && <Banner theme={theme} kind="error">{error}</Banner>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 12,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Pane
          theme={theme}
          title="ORIGINAL"
          imageUrl={originalUrl}
          fileName={file?.name}
          placeholder="Upload a PNG to begin."
        />
        <Pane
          theme={theme}
          title={busy ? "ENDESGA-32 (RUNNING…)" : "ENDESGA-32"}
          imageUrl={snappedUrl}
          fileName={snappedUrl ? downloadName : undefined}
          downloadUrl={snappedUrl ?? undefined}
          downloadName={downloadName}
          placeholder={
            file ? (busy ? "Snapping…" : "Waiting for output.") : "Upload a PNG to begin."
          }
        />
      </div>
    </div>
  );
}

function Pane({
  theme,
  title,
  imageUrl,
  fileName,
  downloadUrl,
  downloadName,
  placeholder,
}: {
  theme: typeof pixelTheme;
  title: string;
  imageUrl: string | null;
  fileName?: string;
  downloadUrl?: string;
  downloadName?: string;
  placeholder: string;
}) {
  return (
    <div
      style={{
        ...simplePanelStyle(theme),
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
          }}
        >
          {title}
        </div>
        {downloadUrl && (
          <a
            href={downloadUrl}
            download={downloadName}
            style={{
              ...pillButtonStyle(theme, false),
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            DOWNLOAD
          </a>
        )}
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: E32.parchment,
          border: `2px solid ${theme.panelEdge}`,
          padding: 8,
          overflow: "auto",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              imageRendering: "pixelated",
            }}
          />
        ) : (
          <div style={{ color: theme.inkFaint, fontSize: 12 }}>{placeholder}</div>
        )}
      </div>
      {fileName && (
        <div
          style={{
            fontSize: 11,
            color: theme.inkFaint,
            wordBreak: "break-all",
          }}
        >
          {fileName}
        </div>
      )}
    </div>
  );
}

// True iff the drag event carries at least one file. Without this
// check, dragging text/HTML/anything else into the tool would still
// trigger the highlight + (ineffective) drop handling.
function hasFileItem(e: React.DragEvent): boolean {
  const items = e.dataTransfer?.items;
  if (!items) {
    // Some browsers (and older Safari) populate `types` instead of
    // `items` during dragenter/dragover.
    return Array.from(e.dataTransfer?.types ?? []).includes("Files");
  }
  for (let i = 0; i < items.length; i++) {
    if (items[i].kind === "file") return true;
  }
  return false;
}

function pillButtonStyle(
  theme: typeof pixelTheme,
  disabled: boolean,
): React.CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
    padding: "6px 10px",
    background: disabled ? theme.panel : theme.accent,
    color: disabled ? theme.inkFaint : E32.parchment,
    border: `2px solid ${disabled ? theme.panelEdge : theme.accent}`,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
